/**
 * Demo client — a drop-in stand-in for ApiClient backed by the in-memory
 * dataset in ./data.ts. Active only when VITE_DEMO=1 (see lib/api.ts). Lets the
 * full app be explored without a server/database. Mutations live for the
 * session (reset on full page reload); the signed-in user persists in
 * localStorage so refreshes keep you logged in.
 */
import type { ApiClient } from "@clinic/api-client";
import {
  APPOINTMENT_TYPE_LABELS,
  type Appointment,
  type AuthResponse,
  type AuthUser,
  type ChatMessage,
  type Conversation,
  type EyeRecord,
  type InsightBucket,
  type InsightsReport,
  type MailDetail,
  type MailSummary,
  type MailUserLite,
  type Patient,
  type PatientWithCounts,
  type Prescription,
} from "@clinic/types";
import { createInitialState, type DemoConversation, type DemoMail, type DemoState } from "./data";

const CURRENT_KEY = "otzem-demo-user";

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), 120));
}
function fail(message: string): never {
  throw new Error(message);
}

export function createDemoClient(): ApiClient {
  const state: DemoState = createInitialState();
  const nextId = (prefix: string) => `${prefix}_${++state.seq}`;
  const userById = (id: string) => state.users.find((u) => u.id === id);

  const getCurrentId = (): string | null => localStorage.getItem(CURRENT_KEY);
  const me = (): AuthUser => {
    const u = getCurrentId() ? userById(getCurrentId()!) : null;
    return u ? { id: u.id, email: u.email, name: u.name, role: u.role } : fail("Not authenticated");
  };
  const lite = (id: string): MailUserLite => {
    const u = userById(id)!;
    return { id: u.id, name: u.name, role: u.role, email: u.email };
  };

  const withCounts = (p: Patient): PatientWithCounts => ({
    ...p,
    _count: {
      eyeRecords: state.records.filter((r) => r.patientId === p.id).length,
      appointments: state.appointments.filter((a) => a.patientId === p.id).length,
      documents: 0,
      prescriptions: state.prescriptions.filter((r) => r.patientId === p.id).length,
    },
  });

  /* ---------------- chat helpers ---------------- */
  const convoToDto = (c: DemoConversation, meId: string): Conversation => {
    const others = c.memberIds.filter((id) => id !== meId).map((id) => userById(id)!);
    const msgs = state.messages
      .filter((m) => m.conversationId === c.id)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const last = msgs[msgs.length - 1];
    const lastRead = c.lastReadByMe[meId] ?? null;
    const unreadCount = msgs.filter(
      (m) => m.senderId !== meId && (!lastRead || m.createdAt > lastRead),
    ).length;
    return {
      id: c.id,
      isGroup: c.isGroup,
      title: c.isGroup
        ? c.title || others.map((o) => o.name).join(", ")
        : others[0]?.name ?? "Conversation",
      otherMembers: others.map((o) => ({ id: o.id, name: o.name, role: o.role })),
      lastMessage: last
        ? { ...last, sender: { id: last.senderId, name: userById(last.senderId)!.name, role: userById(last.senderId)!.role } }
        : null,
      unreadCount,
      updatedAt: c.updatedAt,
    };
  };

  /* ---------------- mail helpers ---------------- */
  const mailSummary = (mail: DemoMail, viewerId: string): MailSummary => {
    const mine = mail.recipients.find((r) => r.userId === viewerId);
    const flat = mail.body.replace(/\s+/g, " ").trim();
    return {
      id: mail.id,
      subject: mail.subject,
      snippet: flat.length > 140 ? `${flat.slice(0, 140)}…` : flat,
      sender: lite(mail.senderId),
      recipients: mail.recipients.map((r) => lite(r.userId)),
      createdAt: mail.createdAt,
      read: mine ? Boolean(mine.readAt) : true,
      starred: mine ? mine.starred : false,
    };
  };

  /* ---------------- insights ---------------- */
  const computeInsights = (): InsightsReport => {
    const nowD = new Date();
    const startToday = new Date(nowD);
    startToday.setHours(0, 0, 0, 0);
    const in7 = new Date(startToday.getTime() + 7 * 86_400_000);
    const in14 = new Date(startToday.getTime() + 14 * 86_400_000);
    const days30 = new Date(nowD.getTime() - 30 * 86_400_000);
    const monthStart = new Date(nowD.getFullYear(), nowD.getMonth(), 1);
    const ageOf = (dob: string) => {
      const d = new Date(dob);
      let a = nowD.getFullYear() - d.getFullYear();
      if (nowD.getMonth() < d.getMonth() || (nowD.getMonth() === d.getMonth() && nowD.getDate() < d.getDate())) a--;
      return a;
    };
    const city = (addr: string | null) => {
      if (!addr) return "Unknown";
      const parts = addr.split(",").map((p) => p.trim()).filter(Boolean);
      return parts[parts.length - 1] ?? "Unknown";
    };
    const diagCat = (d: string) => {
      const s = d.toLowerCase();
      if (s.includes("cataract")) return "Cataract";
      if (s.includes("glaucoma") || s.includes("hypertension")) return "Glaucoma / OHT";
      if (s.includes("dry eye")) return "Dry eye";
      if (s.includes("diabet") || s.includes("retinopathy")) return "Diabetic / retina";
      if (s.includes("macular")) return "Macular";
      if (s.includes("myop")) return "Myopia";
      if (s.includes("hyperop") || s.includes("presbyop")) return "Hyperopia / presbyopia";
      if (s.includes("astigmat")) return "Astigmatism";
      return "Other";
    };
    const top = (m: Map<string, number>): InsightBucket[] =>
      [...m.entries()].map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count).slice(0, 8);

    const patients = state.patients.filter((p) => !p.archivedAt);
    const appts = state.appointments;
    const thisWeek = appts.filter((a) => new Date(a.startTime) >= startToday && new Date(a.startTime) < in7 && a.status !== "CANCELLED");
    const nextWeek = appts.filter((a) => new Date(a.startTime) >= in7 && new Date(a.startTime) < in14 && a.status !== "CANCELLED");

    const followMap = new Map<string, { patientId: string; fullName: string; reason: string; date: string }>();
    for (const a of appts) {
      if (a.type === "FOLLOW_UP" && new Date(a.startTime) >= startToday && new Date(a.startTime) < in14 && (a.status === "SCHEDULED" || a.status === "CONFIRMED")) {
        followMap.set(a.patientId, { patientId: a.patientId, fullName: a.patient?.fullName ?? "Patient", reason: "Scheduled follow-up", date: a.startTime });
      }
    }

    const latest = new Map<string, EyeRecord>();
    for (const r of [...state.records].sort((a, b) => b.visitDate.localeCompare(a.visitDate))) {
      if (!latest.has(r.patientId)) latest.set(r.patientId, r);
    }
    const nameById = new Map(patients.map((p) => [p.id, p.fullName]));
    const iopWatch = [];
    for (const [pid, r] of latest) {
      const right = r.rightEye.iop ?? 0;
      const left = r.leftEye.iop ?? 0;
      const max = Math.max(right, left);
      if (max >= 21) iopWatch.push({ patientId: pid, fullName: nameById.get(pid) ?? "Patient", iop: max, eye: (right >= left ? "OD" : "OS") as "OD" | "OS" });
    }
    iopWatch.sort((a, b) => b.iop - a.iop);

    const ages = patients.map((p) => ageOf(p.dob));
    const averageAge = ages.length ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0;
    const ageDistribution: InsightBucket[] = [
      { label: "0–17", min: 0, max: 17 },
      { label: "18–39", min: 18, max: 39 },
      { label: "40–59", min: 40, max: 59 },
      { label: "60–79", min: 60, max: 79 },
      { label: "80+", min: 80, max: 200 },
    ].map((b) => ({ label: b.label, count: ages.filter((a) => a >= b.min && a <= b.max).length }));

    const cityCounts = new Map<string, number>();
    patients.forEach((p) => cityCounts.set(city(p.address), (cityCounts.get(city(p.address)) ?? 0) + 1));
    const diagCounts = new Map<string, number>();
    [...latest.values()].forEach((r) => diagCounts.set(diagCat(r.diagnosis), (diagCounts.get(diagCat(r.diagnosis)) ?? 0) + 1));
    const typeCounts = new Map<string, number>();
    appts.forEach((a) => {
      const l = APPOINTMENT_TYPE_LABELS[a.type];
      typeCounts.set(l, (typeCounts.get(l) ?? 0) + 1);
    });

    const recent = appts.filter((a) => new Date(a.startTime) >= days30);
    const rTotal = recent.length || 1;
    const cancellationRate = recent.filter((a) => a.status === "CANCELLED").length / rTotal;
    const noShowRate = recent.filter((a) => a.status === "NO_SHOW").length / rTotal;

    const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayCounts = new Map<number, number>();
    [...thisWeek, ...nextWeek].forEach((a) => {
      const d = new Date(a.startTime).getDay();
      dayCounts.set(d, (dayCounts.get(d) ?? 0) + 1);
    });
    let busiestDay: InsightsReport["busiestDay"] = null;
    for (const [d, count] of dayCounts) if (!busiestDay || count > busiestDay.count) busiestDay = { label: DAYS[d]!, count };

    const geographic = top(cityCounts);
    const topCity = geographic[0];
    const summary: string[] = [
      `${thisWeek.length} patients booked this week and ${nextWeek.length} next week.`,
    ];
    if (followMap.size) summary.push(`${followMap.size} follow-ups need attention${iopWatch.length ? `, including ${iopWatch.length} elevated-IOP cases` : ""}.`);
    if (topCity) summary.push(`Most patients come from ${topCity.label} (${Math.round((topCity.count / patients.length) * 100)}% of your base).`);
    summary.push(`Average patient age is ${averageAge}; ${ages.filter((a) => a >= 40).length} are 40 or older.`);
    if (iopWatch.length) summary.push(`${iopWatch.length} patients show IOP ≥ 21 mmHg — consider a glaucoma work-up.`);
    summary.push(`Cancellation rate is ${Math.round(cancellationRate * 100)}% and no-shows ${Math.round(noShowRate * 100)}% over the last 30 days.`);

    return {
      generatedAt: nowD.toISOString(),
      headline: "Here's your clinic at a glance",
      summary,
      thisWeekCount: thisWeek.length,
      nextWeekCount: nextWeek.length,
      followUpsDue: [...followMap.values()].sort((a, b) => a.date.localeCompare(b.date)),
      totalPatients: patients.length,
      newPatientsThisMonth: patients.filter((p) => new Date(p.createdAt) >= monthStart).length,
      averageAge,
      ageDistribution,
      geographic,
      diagnoses: top(diagCounts),
      appointmentTypes: top(typeCounts),
      iopWatch,
      cancellationRate,
      noShowRate,
      busiestDay,
    };
  };

  /* ================================================================== */
  const client = {
    auth: {
      login: (input: { email: string; password: string }): Promise<AuthResponse> => {
        const u = state.users.find((x) => x.email.toLowerCase() === input.email.toLowerCase());
        if (!u) return Promise.reject(new Error("No demo account for that email. Try the quick-login buttons."));
        localStorage.setItem(CURRENT_KEY, u.id);
        return delay({ user: { id: u.id, email: u.email, name: u.name, role: u.role }, accessToken: "demo", refreshToken: "demo" });
      },
      logout: () => {
        localStorage.removeItem(CURRENT_KEY);
        return delay(undefined as unknown as void);
      },
      me: () => delay(me()),
    },

    patients: {
      list: (params?: { search?: string; includeArchived?: boolean }) => {
        let rows = state.patients.filter((p) => params?.includeArchived || !p.archivedAt);
        const s = params?.search?.toLowerCase().trim();
        if (s) rows = rows.filter((p) => [p.fullName, p.idNumber, p.phone, p.email ?? ""].some((f) => f.toLowerCase().includes(s)));
        return delay(rows.map(withCounts));
      },
      get: (id: string) => {
        const p = state.patients.find((x) => x.id === id);
        return p ? delay(withCounts(p)) : Promise.reject(new Error("Patient not found"));
      },
      create: (input: Partial<Patient>) => {
        const p: Patient = {
          id: nextId("p"), fullName: input.fullName ?? "New Patient", dob: input.dob ?? new Date().toISOString(),
          idNumber: input.idNumber ?? nextId("id"), phone: input.phone ?? "", email: input.email ?? null,
          address: input.address ?? null, notes: input.notes ?? null, createdAt: new Date().toISOString(), archivedAt: null,
        };
        state.patients.unshift(p);
        return delay(p);
      },
      update: (id: string, input: Partial<Patient>) => {
        const p = state.patients.find((x) => x.id === id)!;
        Object.assign(p, input);
        return delay(p);
      },
      archive: (id: string) => {
        const p = state.patients.find((x) => x.id === id)!;
        p.archivedAt = new Date().toISOString();
        return delay(p);
      },
      restore: (id: string) => {
        const p = state.patients.find((x) => x.id === id)!;
        p.archivedAt = null;
        return delay(p);
      },
      records: (id: string) => delay(state.records.filter((r) => r.patientId === id).sort((a, b) => b.visitDate.localeCompare(a.visitDate))),
      createRecord: (id: string, input: { visitDate?: string; diagnosis: string; rightEye: EyeRecord["rightEye"]; leftEye: EyeRecord["leftEye"]; prescription?: string; notes?: string }) => {
        const rec: EyeRecord = {
          id: nextId("r"), patientId: id, visitDate: input.visitDate || new Date().toISOString(),
          diagnosis: input.diagnosis, rightEye: input.rightEye, leftEye: input.leftEye,
          prescription: input.prescription ?? null, notes: input.notes ?? null, createdAt: new Date().toISOString(),
        };
        state.records.unshift(rec);
        return delay(rec);
      },
      appointments: (id: string) => delay(state.appointments.filter((a) => a.patientId === id)),
      documents: () => delay([]),
      uploadDocument: (id: string) => delay({ id: nextId("doc"), patientId: id, filename: "demo.pdf", mimeType: "application/pdf", url: "#", uploadedAt: new Date().toISOString() }),
      deleteDocument: () => delay(undefined as unknown as void),
      prescriptions: (id: string) => delay(state.prescriptions.filter((r) => r.patientId === id)),
    },

    appointments: {
      list: (params?: { from?: string; to?: string; doctorId?: string; status?: string }) => {
        let rows = state.appointments;
        if (params?.from) rows = rows.filter((a) => a.startTime >= params.from!);
        if (params?.to) rows = rows.filter((a) => a.startTime <= params.to!);
        if (params?.status) rows = rows.filter((a) => a.status === params.status);
        return delay(rows);
      },
      create: (input: { patientId: string; doctorId: string; startTime: string; endTime: string; type: Appointment["type"]; notes?: string }) => {
        const p = state.patients.find((x) => x.id === input.patientId);
        const doc = userById(input.doctorId);
        const a: Appointment = {
          id: nextId("a"), patientId: input.patientId, doctorId: input.doctorId, startTime: input.startTime, endTime: input.endTime,
          type: input.type, status: "SCHEDULED", notes: input.notes ?? null, reminderSent24h: false, reminderSent1w: false, createdAt: new Date().toISOString(),
          patient: p ? { id: p.id, fullName: p.fullName, phone: p.phone, email: p.email } : undefined,
          doctor: doc ? { id: doc.id, name: doc.name } : undefined,
        };
        state.appointments.push(a);
        return delay(a);
      },
      update: (id: string, input: Partial<Appointment>) => {
        const a = state.appointments.find((x) => x.id === id)!;
        Object.assign(a, input);
        return delay(a);
      },
      cancel: (id: string) => {
        const a = state.appointments.find((x) => x.id === id)!;
        a.status = "CANCELLED";
        return delay(a);
      },
    },

    public_: {
      getAppointmentByToken: () => Promise.reject(new Error("Not available in demo")),
      cancelByToken: () => delay({ ok: true }),
    },

    prescriptions: {
      list: (params?: { patientId?: string }) =>
        delay(params?.patientId ? state.prescriptions.filter((r) => r.patientId === params.patientId) : state.prescriptions),
      create: (input: { patientId: string; eyeRecordId: string }) => {
        const p = state.patients.find((x) => x.id === input.patientId)!;
        const doc = state.users[1]!;
        const rec = state.records.find((r) => r.id === input.eyeRecordId);
        const rx: Prescription = {
          id: nextId("rx"), patientId: input.patientId, eyeRecordId: input.eyeRecordId, doctorId: doc.id, issuedAt: new Date().toISOString(),
          pdfUrl: "#demo-pdf",
          patient: { id: p.id, fullName: p.fullName, idNumber: p.idNumber, dob: p.dob, phone: p.phone },
          doctor: { id: doc.id, name: doc.name },
          ...(rec ? { eyeRecord: rec } : {}),
        };
        state.prescriptions.unshift(rx);
        return delay(rx);
      },
    },

    dashboard: {
      stats: () => {
        const nowD = new Date();
        const startToday = new Date(nowD); startToday.setHours(0, 0, 0, 0);
        const endToday = new Date(startToday.getTime() + 86_400_000);
        const weekEnd = new Date(startToday.getTime() + 7 * 86_400_000);
        const monthStart = new Date(nowD.getFullYear(), nowD.getMonth(), 1);
        const days30 = new Date(nowD.getTime() - 30 * 86_400_000);
        const active = state.patients.filter((p) => !p.archivedAt);
        const todayAppts = state.appointments
          .filter((a) => new Date(a.startTime) >= startToday && new Date(a.startTime) < endToday && a.status !== "CANCELLED")
          .sort((a, b) => a.startTime.localeCompare(b.startTime));
        const weekAppts = state.appointments.filter((a) => new Date(a.startTime) >= startToday && new Date(a.startTime) < weekEnd && a.status !== "CANCELLED");
        const byDay = Array.from({ length: 7 }).map((_, i) => {
          const d = new Date(startToday.getTime() + i * 86_400_000);
          const next = new Date(d.getTime() + 86_400_000);
          return { date: d.toISOString(), count: state.appointments.filter((a) => new Date(a.startTime) >= d && new Date(a.startTime) < next && a.status !== "CANCELLED").length };
        });
        const recent = state.appointments.filter((a) => new Date(a.startTime) >= days30);
        const cancellationRate = (recent.filter((a) => a.status === "CANCELLED").length) / (recent.length || 1);
        return delay({
          todayCount: todayAppts.length,
          weekCount: weekAppts.length,
          totalPatients: active.length,
          newPatientsThisMonth: active.filter((p) => new Date(p.createdAt) >= monthStart).length,
          cancellationRate,
          appointmentsByDay: byDay,
          todayAppointments: todayAppts,
          recentActivity: state.audit.slice(0, 6),
        });
      },
    },

    insights: { report: () => delay(computeInsights()) },

    audit: {
      list: (params?: { entity?: string; page?: number; pageSize?: number }) => {
        let items = state.audit;
        if (params?.entity) items = items.filter((a) => a.entity === params.entity);
        const page = params?.page ?? 1;
        const pageSize = params?.pageSize ?? 20;
        return delay({ items: items.slice((page - 1) * pageSize, page * pageSize), total: items.length, page, pageSize });
      },
    },

    settings: {
      getClinic: () => delay(state.clinic),
      updateClinic: (input: Partial<typeof state.clinic>) => {
        Object.assign(state.clinic, input, { updatedAt: new Date().toISOString() });
        return delay(state.clinic);
      },
      getAvailability: () => delay(state.availability),
      setAvailability: (blocks: typeof state.availability) => {
        state.availability = blocks;
        return delay(state.availability);
      },
      emailPreviewUrl: (template: string) =>
        `data:text/html;charset=utf-8,${encodeURIComponent(
          `<div style="font-family:system-ui;padding:40px;color:#1a2b30"><h2>Email preview — ${template}</h2><p>Live email previews render from the API server. In demo mode this is a placeholder.</p></div>`,
        )}`,
    },

    users: {
      doctors: () => delay(state.users.filter((u) => u.role === "DOCTOR").map((u) => ({ id: u.id, name: u.name, role: u.role }))),
      directory: () => {
        const meId = getCurrentId();
        return delay(state.users.filter((u) => u.id !== meId).map((u) => lite(u.id)));
      },
      list: () => delay(state.users.map((u) => ({ id: u.id, email: u.email, name: u.name, role: u.role, createdAt: u.createdAt }))),
      create: (input: { email: string; name: string; role: AuthUser["role"]; password: string }) => {
        const u = { id: nextId("u"), email: input.email, name: input.name, role: input.role, createdAt: new Date().toISOString() };
        state.users.push(u);
        return delay({ id: u.id, email: u.email, name: u.name, role: u.role, createdAt: u.createdAt });
      },
      update: (id: string, input: { name?: string; role?: AuthUser["role"]; email?: string }) => {
        const u = userById(id)!;
        Object.assign(u, input);
        return delay({ id: u.id, email: u.email, name: u.name, role: u.role, createdAt: u.createdAt });
      },
      remove: (id: string) => {
        state.users = state.users.filter((u) => u.id !== id);
        return delay(undefined as unknown as void);
      },
    },

    chat: {
      conversations: () => {
        const meId = me().id;
        return delay(
          state.conversations
            .filter((c) => c.memberIds.includes(meId))
            .map((c) => convoToDto(c, meId))
            .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
        );
      },
      start: (input: { participantIds: string[]; title?: string }) => {
        const meId = me().id;
        const others = [...new Set(input.participantIds)].filter((id) => id !== meId);
        const isGroup = others.length > 1 || Boolean(input.title?.trim());
        if (!isGroup) {
          const existing = state.conversations.find((c) => !c.isGroup && c.memberIds.length === 2 && c.memberIds.includes(meId) && c.memberIds.includes(others[0]!));
          if (existing) return delay(convoToDto(existing, meId));
        }
        const c: DemoConversation = {
          id: nextId("c"), isGroup, title: isGroup ? input.title?.trim() || null : null,
          memberIds: [meId, ...others], lastReadByMe: { [meId]: new Date().toISOString() }, updatedAt: new Date().toISOString(),
        };
        state.conversations.unshift(c);
        return delay(convoToDto(c, meId));
      },
      messages: (conversationId: string) => {
        const meId = me().id;
        const c = state.conversations.find((x) => x.id === conversationId);
        if (c) c.lastReadByMe[meId] = new Date().toISOString();
        return delay(
          state.messages
            .filter((m) => m.conversationId === conversationId)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
            .map((m) => ({ ...m, sender: { id: m.senderId, name: userById(m.senderId)!.name, role: userById(m.senderId)!.role } }) as ChatMessage),
        );
      },
      send: (conversationId: string, input: { body: string }) => {
        const meId = me().id;
        const msg = { id: nextId("m"), conversationId, senderId: meId, body: input.body, createdAt: new Date().toISOString() };
        state.messages.push(msg);
        const c = state.conversations.find((x) => x.id === conversationId);
        if (c) { c.updatedAt = msg.createdAt; c.lastReadByMe[meId] = msg.createdAt; }
        return delay({ ...msg, sender: { id: meId, name: userById(meId)!.name, role: userById(meId)!.role } } as ChatMessage);
      },
      unreadCount: () => {
        const meId = getCurrentId();
        if (!meId) return delay({ count: 0 });
        let count = 0;
        for (const c of state.conversations.filter((x) => x.memberIds.includes(meId))) {
          const lastRead = c.lastReadByMe[meId] ?? null;
          count += state.messages.filter((m) => m.conversationId === c.id && m.senderId !== meId && (!lastRead || m.createdAt > lastRead)).length;
        }
        return delay({ count });
      },
    },

    mail: {
      inbox: () => {
        const meId = me().id;
        return delay(
          state.mails
            .filter((m) => m.recipients.some((r) => r.userId === meId && !r.deletedAt))
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
            .map((m) => mailSummary(m, meId)),
        );
      },
      sent: () => {
        const meId = me().id;
        return delay(
          state.mails.filter((m) => m.senderId === meId && !m.senderDeletedAt).sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map((m) => mailSummary(m, meId)),
        );
      },
      get: (id: string) => {
        const meId = me().id;
        const mail = state.mails.find((m) => m.id === id);
        if (!mail) return Promise.reject(new Error("Message not found"));
        const mine = mail.recipients.find((r) => r.userId === meId);
        if (mine && !mine.readAt) mine.readAt = new Date().toISOString();
        return delay({ ...mailSummary(mail, meId), body: mail.body, parentId: mail.parentId } as MailDetail);
      },
      send: (input: { recipientIds: string[]; subject: string; body: string; parentId?: string }) => {
        const meId = me().id;
        const mail: DemoMail = {
          id: nextId("ml"), senderId: meId, subject: input.subject, body: input.body, parentId: input.parentId ?? null, createdAt: new Date().toISOString(),
          recipients: [...new Set(input.recipientIds)].filter((id) => id !== meId).map((userId) => ({ userId, readAt: null, starred: false, deletedAt: null })),
          senderDeletedAt: null,
        };
        state.mails.unshift(mail);
        return delay({ ...mailSummary(mail, meId), body: mail.body, parentId: mail.parentId } as MailDetail);
      },
      star: (id: string) => {
        const meId = me().id;
        const r = state.mails.find((m) => m.id === id)?.recipients.find((x) => x.userId === meId);
        if (r) r.starred = true;
        return delay({ ok: true });
      },
      unstar: (id: string) => {
        const meId = me().id;
        const r = state.mails.find((m) => m.id === id)?.recipients.find((x) => x.userId === meId);
        if (r) r.starred = false;
        return delay({ ok: true });
      },
      remove: (id: string) => {
        const meId = me().id;
        const mail = state.mails.find((m) => m.id === id);
        if (mail) {
          const r = mail.recipients.find((x) => x.userId === meId);
          if (r) r.deletedAt = new Date().toISOString();
          if (mail.senderId === meId) mail.senderDeletedAt = new Date().toISOString();
        }
        return delay(undefined as unknown as void);
      },
      unreadCount: () => {
        const meId = getCurrentId();
        if (!meId) return delay({ count: 0 });
        const count = state.mails.reduce((acc, m) => acc + m.recipients.filter((r) => r.userId === meId && !r.deletedAt && !r.readAt).length, 0);
        return delay({ count });
      },
    },

    push: { register: () => delay({ ok: true }) },
  };

  return client as unknown as ApiClient;
}
